import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ActivityResponse, CommentResponse, FeedsClient } from '@stream-io/feeds-client'
import { supabase } from '@/integrations/supabase/client'

/**
 * One GetStream Feeds v3 client for the whole app. Lazy: nothing connects
 * until a consumer calls getClient(). Reaction and comment counts come from
 * the activity payloads Stream returns (reaction_groups, own_reactions,
 * comment_count), cached here so every consumer sees the same numbers.
 *
 * The SDK (~170 KiB minified) is also loaded lazily, inside getClient(), so
 * the entry chunk carries only its types. This provider mounts on every
 * route; the client is only ever built for a signed-in user who reaches a
 * feeds surface.
 */

export interface ActivityStats {
  likes: number
  liked: boolean
  comments: number
  bookmarks: number
  bookmarked: boolean
}

export const EMPTY_STATS: ActivityStats = { likes: 0, liked: false, comments: 0, bookmarks: 0, bookmarked: false }

export const statsFromActivity = (activity: ActivityResponse): ActivityStats => ({
  likes: activity.reaction_groups?.like?.count ?? 0,
  liked: (activity.own_reactions ?? []).some((r) => r.type === 'like'),
  comments: activity.comment_count ?? 0,
  bookmarks: activity.bookmark_count ?? 0,
  bookmarked: (activity.own_bookmarks ?? []).length > 0,
})

export interface FeedsContextValue {
  /** Connects on first use. Resolves null when there is no session. */
  getClient: () => Promise<FeedsClient | null>
  userId: string | null
  stats: Record<string, ActivityStats>
  /** Seed stats from activities loaded elsewhere (feed pages, build log). */
  recordActivities: (activities: ActivityResponse[]) => void
  loadActivities: (ids: string[]) => Promise<ActivityResponse[]>
  like: (activityId: string) => Promise<void>
  unlike: (activityId: string) => Promise<void>
  bookmark: (activityId: string) => Promise<void>
  unbookmark: (activityId: string) => Promise<void>
  addComment: (activityId: string, text: string) => Promise<CommentResponse>
  listComments: (activityId: string) => Promise<CommentResponse[]>
  deleteComment: (commentId: string, activityId: string) => Promise<void>
}

const FeedsContext = createContext<FeedsContextValue | null>(null)

// Matches the chat and edge-function limit (20 writes per minute per user).
const COMMENT_LIMIT = 20
const COMMENT_WINDOW_MS = 60_000

export function FeedsProvider({ children }: { children: React.ReactNode }) {
  const clientRef = useRef<FeedsClient | null>(null)
  const connectingRef = useRef<Promise<FeedsClient | null> | null>(null)
  const connectedIdRef = useRef<string | null>(null)
  const commentTimesRef = useRef<number[]>([])
  const [userId, setUserId] = useState<string | null>(null)
  const [stats, setStats] = useState<Record<string, ActivityStats>>({})

  const disconnect = useCallback(() => {
    const client = clientRef.current
    clientRef.current = null
    connectingRef.current = null
    connectedIdRef.current = null
    setUserId(null)
    client?.disconnectUser().catch(() => undefined)
  }, [])

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        disconnect()
        return
      }
      if (event === 'SIGNED_IN') {
        // A cached "no session" result must not outlive the sign-in, and a
        // different account must not inherit the previous user's client.
        if (!clientRef.current) connectingRef.current = null
        else if (session?.user?.id && session.user.id !== connectedIdRef.current) disconnect()
      }
    })
    return () => {
      data.subscription.unsubscribe()
      disconnect()
    }
  }, [disconnect])

  const getClient = useCallback((): Promise<FeedsClient | null> => {
    if (clientRef.current) return Promise.resolve(clientRef.current)
    if (connectingRef.current) return connectingRef.current

    connectingRef.current = (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session) {
        connectingRef.current = null
        return null
      }

      const { data: tokenData, error } = await supabase.functions.invoke('generate-getstream-token', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (error) throw new Error(`Failed to get GetStream token: ${error.message}`)
      const { userId: id, feedsToken, feedsApiKey } = tokenData ?? {}
      if (!feedsApiKey || !feedsToken) throw new Error('GetStream Feeds credentials missing from token response')

      const { FeedsClient } = await import('@stream-io/feeds-client')
      const client = new FeedsClient(feedsApiKey)
      await client.connectUser({ id }, feedsToken)
      clientRef.current = client
      connectedIdRef.current = id
      setUserId(id)
      return client
    })().catch((err) => {
      connectingRef.current = null
      throw err
    })
    return connectingRef.current
  }, [])

  const recordActivities = useCallback((activities: ActivityResponse[]) => {
    if (activities.length === 0) return
    setStats((prev) => {
      const next = { ...prev }
      for (const activity of activities) next[activity.id] = statsFromActivity(activity)
      return next
    })
  }, [])

  const patchStats = useCallback((activityId: string, patch: (s: ActivityStats) => ActivityStats) => {
    setStats((prev) => ({ ...prev, [activityId]: patch(prev[activityId] ?? EMPTY_STATS) }))
  }, [])

  const requireClient = useCallback(async () => {
    const client = await getClient()
    if (!client) throw new Error('Sign in to do that')
    return client
  }, [getClient])

  const loadActivities = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return []
      const client = await requireClient()
      const res = await client.queryActivities({ filter: { id: { $in: ids } }, limit: ids.length })
      const activities = res.activities ?? []
      recordActivities(activities)
      return activities
    },
    [requireClient, recordActivities],
  )

  // Reaction responses carry the updated activity; fall back to a local
  // adjustment when the server omits it.
  const like = useCallback(
    async (activityId: string) => {
      const client = await requireClient()
      const res = await client.addActivityReaction({ activity_id: activityId, type: 'like', create_notification_activity: true })
      if (res?.activity) recordActivities([res.activity])
      else patchStats(activityId, (s) => (s.liked ? s : { ...s, liked: true, likes: s.likes + 1 }))
    },
    [requireClient, recordActivities, patchStats],
  )

  const unlike = useCallback(
    async (activityId: string) => {
      const client = await requireClient()
      const res = await client.deleteActivityReaction({ activity_id: activityId, type: 'like' })
      if (res?.activity) recordActivities([res.activity])
      else patchStats(activityId, (s) => (s.liked ? { ...s, liked: false, likes: Math.max(0, s.likes - 1) } : s))
    },
    [requireClient, recordActivities, patchStats],
  )

  const bookmark = useCallback(
    async (activityId: string) => {
      const client = await requireClient()
      await client.addBookmark({ activity_id: activityId })
      patchStats(activityId, (s) => (s.bookmarked ? s : { ...s, bookmarked: true, bookmarks: s.bookmarks + 1 }))
    },
    [requireClient, patchStats],
  )

  const unbookmark = useCallback(
    async (activityId: string) => {
      const client = await requireClient()
      await client.deleteBookmark({ activity_id: activityId })
      patchStats(activityId, (s) => (s.bookmarked ? { ...s, bookmarked: false, bookmarks: Math.max(0, s.bookmarks - 1) } : s))
    },
    [requireClient, patchStats],
  )

  const addComment = useCallback(
    async (activityId: string, text: string) => {
      const now = Date.now()
      commentTimesRef.current = commentTimesRef.current.filter((t) => now - t < COMMENT_WINDOW_MS)
      if (commentTimesRef.current.length >= COMMENT_LIMIT) throw new Error('Too many comments. Try again in a minute.')
      const client = await requireClient()
      const res = await client.addComment({
        object_id: activityId,
        object_type: 'activity',
        comment: text,
        create_notification_activity: true,
      })
      commentTimesRef.current.push(now)
      patchStats(activityId, (s) => ({ ...s, comments: s.comments + 1 }))
      return res.comment
    },
    [requireClient, patchStats],
  )

  const listComments = useCallback(
    async (activityId: string) => {
      const client = await requireClient()
      const res = await client.getComments({ object_id: activityId, object_type: 'activity', sort: 'first', limit: 100 })
      // The server's comment_count stays authoritative; this page is capped at 100.
      return (res.comments ?? []) as CommentResponse[]
    },
    [requireClient, patchStats],
  )

  const deleteComment = useCallback(
    async (commentId: string, activityId: string) => {
      const client = await requireClient()
      await client.deleteComment({ id: commentId })
      patchStats(activityId, (s) => ({ ...s, comments: Math.max(0, s.comments - 1) }))
    },
    [requireClient, patchStats],
  )

  const value = useMemo<FeedsContextValue>(
    () => ({
      getClient,
      userId,
      stats,
      recordActivities,
      loadActivities,
      like,
      unlike,
      bookmark,
      unbookmark,
      addComment,
      listComments,
      deleteComment,
    }),
    [getClient, userId, stats, recordActivities, loadActivities, like, unlike, bookmark, unbookmark, addComment, listComments, deleteComment],
  )

  return <FeedsContext.Provider value={value}>{children}</FeedsContext.Provider>
}

export function useFeeds(): FeedsContextValue {
  const ctx = useContext(FeedsContext)
  if (!ctx) throw new Error('useFeeds must be used within FeedsProvider')
  return ctx
}

export default FeedsProvider
